import { useEffect, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';

// Whether the internal chat surface is available right now.
//
// Chat is switched OFF portal-wide while Maintenance Lockdown is ON (the Super
// Admin toggle in the ERP). When locked, the whole chat surface — the open
// button, the "G then C" shortcut and the panel itself — disappears as if the
// feature does not exist. No conversation data is ever touched: turning the
// lockdown OFF brings chat back exactly as it was.
//
// The last known state is primed from localStorage so a locked portal never
// flashes the chat button on reload. We re-check on a short poll and whenever
// the tab regains focus/visibility, so flipping the lockdown reflects quickly
// on every device (phone, tablet, laptop) without a manual refresh.
const LS_KEY = 'notes:chatEnabled';

function readInit() {
  try { return localStorage.getItem(LS_KEY) !== '0'; } catch { return true; }
}

export function useChatEnabled() {
  const [enabled, setEnabled] = useState(readInit);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    let stop = false;
    const apply = (on) => {
      enabledRef.current = on;
      setEnabled(on);
      try { localStorage.setItem(LS_KEY, on ? '1' : '0'); } catch { /* noop */ }
    };
    const check = async () => {
      try {
        const { data } = await notesApi.get('/notes/chat/availability');
        if (stop) return;
        apply(data?.enabled !== false);
      } catch { /* keep last known state on error */ }
    };
    check();
    const iv = setInterval(check, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const onFocus = () => check();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      stop = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return { enabled, enabledRef };
}
